import { useState } from 'react'

import { useParams } from 'react-router-dom'

import { useProductInfoUsageApplicationsApi } from '@/common/api/modules/productInfoUsage'
import {
  PiuApplication,
  PIU_FIELD_LABEL,
  PIU_TARGET_LABEL,
  PIU_USAGE_LABEL
} from '@/common/model/productInfoUsage'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

const STATUS: Record<string, { label: string; badgeType: BadgeType }> = {
  running: { label: '実行中', badgeType: BadgeType.SQUARE_CAUTION },
  complete: { label: '完了', badgeType: BadgeType.SQUARE_SUCCESS },
  error: { label: '失敗', badgeType: BadgeType.SQUARE_CRITICAL }
}

const fmtDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' }) : '—'

const fmtElapsed = (sec: number | null) => (sec === null ? '—' : `${sec.toFixed(1)} 秒`)

/** 反映した時点の設定。今の検索結果がどの設定で作られたかはこれでしか分からない */
const SnapshotDetail = ({ app }: { app: PiuApplication }) => {
  const usageKeys = Object.keys(app.settings_snapshot ?? {})
  if (usageKeys.length === 0) return <Text size={12}>—</Text>
  return (
    <div className='flex flex-col gap-1'>
      {usageKeys.map((u) => {
        const fields = app.settings_snapshot[u] ?? {}
        const off = Object.keys(fields)
          .filter((f) => !fields[f])
          .map((f) => PIU_FIELD_LABEL[f] ?? f)
        return (
          <div key={u}>
            <Text size={12} color={TextColor.SECONDARY}>
              {PIU_USAGE_LABEL[u] ?? u}：{' '}
              {off.length === 0 ? 'すべて適用' : `除外 ${off.join('、')}`}
            </Text>
          </div>
        )
      })}
    </div>
  )
}

const ProductInfoUsageHistorySection = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)
  const [expanded, setExpanded] = useState<number | null>(null)

  const { data } = useProductInfoUsageApplicationsApi({ site_id: siteId, limit: 30 })
  const rows = data ?? []

  return (
    <Area>
      <Heading subText='いつ・誰が・どの設定で索引を作り直したかの記録です。設定の変更そのものは監査ログに残ります。'>
        反映履歴
      </Heading>
      <AreaContent>
        {rows.length === 0 ? (
          <div className='mt-3'>
            <Text size={13} color={TextColor.SECONDARY}>
              まだ反映していません。
            </Text>
          </div>
        ) : (
          <div className='mt-3'>
            <Table>
              <thead>
                <TableRow head>
                  {['実行日時', '反映先', '状態', '所要', '実行者', 'そのときの設定'].map((h) => (
                    <TableHeadItem key={h}>
                      <Text size={12}>{h}</Text>
                    </TableHeadItem>
                  ))}
                </TableRow>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const st = STATUS[a.status] ?? {
                    label: a.status,
                    badgeType: BadgeType.SQUARE_TERTIARY
                  }
                  return (
                    <TableRow key={a.id}>
                      <TableRowItem>
                        <Text size={12}>{fmtDateTime(a.started_at)}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>
                          {a.targets.map((t) => PIU_TARGET_LABEL[t] ?? t).join(' / ')}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Badge badgeType={st.badgeType}>{st.label}</Badge>
                        {a.error && (
                          <div>
                            <Text size={12} color={TextColor.CRITICAL}>
                              {a.error}
                            </Text>
                          </div>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>{fmtElapsed(a.elapsed_sec)}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text size={12}>{a.triggered_by_name ?? '—'}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        {expanded === a.id ? (
                          <div className='flex flex-col gap-1'>
                            <SnapshotDetail app={a} />
                            <div>
                              <Button
                                color={ButtonColor.TERTIARY}
                                onClick={() => setExpanded(null)}
                              >
                                閉じる
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button color={ButtonColor.TERTIARY} onClick={() => setExpanded(a.id)}>
                            表示
                          </Button>
                        )}
                      </TableRowItem>
                    </TableRow>
                  )
                })}
              </tbody>
            </Table>
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default ProductInfoUsageHistorySection
